import { createContext, PropsWithChildren, ReactNode, useCallback, useContext } from "react"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { mobileMenuOpenSelector, setVerticalMenuOpenMobile, verticalMenuOpenSelector } from "store/tempDataSlise"

import { useWindowResize } from "hooks/useWindowResize"

import {
  BetaBadge,
  MenuIcon,
  MenuIconStroke,
  MenuItems,
  MenuLabelWrapper,
  MenuText,
  MobileOverlay,
  RedDot,
  StyledNav,
  StyledNavLink,
} from "./styled"

const VerticalMenuContext = createContext<{ isOpen: boolean; closeMobileMenu: () => void }>({
  isOpen: false,
  closeMobileMenu: () => {},
})

export const useVerticalMenuContext = () => useContext(VerticalMenuContext)

export const VerticalMenu = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch()
  const desktopMenuOpen = useAppSelector(verticalMenuOpenSelector)
  const mobileMenuOpen = useAppSelector(mobileMenuOpenSelector)
  const isLargeScreen = useWindowResize()

  const isOpen = isLargeScreen ? desktopMenuOpen : mobileMenuOpen

  const closeMobileMenu = useCallback(() => {
    if (!isLargeScreen) {
      dispatch(setVerticalMenuOpenMobile(false))
    }
  }, [dispatch, isLargeScreen])

  return (
    <VerticalMenuContext.Provider value={{ isOpen, closeMobileMenu }}>
      <MobileOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
      <StyledNav $isOpen={isOpen}>
        <MenuItems>{children}</MenuItems>
      </StyledNav>
    </VerticalMenuContext.Provider>
  )
}

interface MenuItemProps {
  to: string
  icon: ReactNode
  label: string
  badge?: ReactNode
  iconType?: "fill" | "stroke"
  activeIconColor?: string
  beta?: boolean
}

export const MenuItem = ({ to, icon, label, badge, iconType = "fill", activeIconColor, beta }: MenuItemProps) => {
  const { isOpen, closeMobileMenu } = useVerticalMenuContext()
  const IconWrapper = iconType === "stroke" ? MenuIconStroke : MenuIcon

  const handleClick = () => {
    window.scrollTo({ top: 0 })
    closeMobileMenu()
  }

  return (
    <StyledNavLink to={to} activeClassName="active" $activeIconColor={activeIconColor} onClick={handleClick}>
      <IconWrapper>{icon}</IconWrapper>
      {beta ? (
        <MenuLabelWrapper $isOpen={isOpen}>
          <MenuText as="span" $isOpen={isOpen}>
            {label}
          </MenuText>
          <BetaBadge $isOpen={isOpen}>Beta</BetaBadge>
        </MenuLabelWrapper>
      ) : (
        <>
          <MenuText $isOpen={isOpen}>{label}</MenuText>
          {badge}
        </>
      )}
    </StyledNavLink>
  )
}

interface MenuBadgeProps {
  count?: number
}

export const MenuBadge = ({ count }: MenuBadgeProps) => {
  const { isOpen } = useVerticalMenuContext()

  if (!count) return null

  return <RedDot $isOpen={isOpen}>{count}</RedDot>
}

export {
  BetaBadge,
  MenuIcon,
  MenuIconStroke,
  MenuItems,
  MenuLabelWrapper,
  MenuText,
  RedDot,
  StyledNavLink,
} from "./styled"
